import React, { useState, useEffect, useRef } from "react"

import { PencilIcon } from "@heroicons/react/24/outline"

import type { GridCellProps } from "./types"

export const GridCell: React.FC<GridCellProps> = ({
  month,
  value,
  source,
  displayUnit,
  onValueChange,
  isEditable = true,
  isEditing: isEditingProp,
  onEditStart,
  onEditEnd,
  onTabNext,
  onTabPrev,
}) => {
  const isControlled = isEditingProp !== undefined
  const [internalIsEditing, setInternalIsEditing] = useState(false)
  const isEditing = isControlled ? isEditingProp : internalIsEditing

  const [tempValue, setTempValue] = useState(value?.toString() || ``)
  const [hasUserInput, setHasUserInput] = useState(false)
  const [isHovered, setIsHovered] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  // Update tempValue when value prop changes (e.g., when toggling $ <-> kWh)
  useEffect(() => {
    if (!isEditing) {
      setTempValue(value?.toString() || ``)
      setHasUserInput(false)
    }
  }, [value, isEditing])

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus()
      inputRef.current.select()
    }
  }, [isEditing])

  // Get the display value for the input (rounded to 2 decimal places for readability)
  const getInputDisplayValue = () => {
    if (hasUserInput) {
      return tempValue
    }

    if (value !== null) {
      const rounded = Math.round(value * 100) / 100
      return rounded.toString()
    }
    return ``
  }

  const handleClick = () => {
    if (!isEditable) return
    if (isControlled) {
      onEditStart?.()
    } else {
      setInternalIsEditing(true)
    }
  }

  const handleSave = () => {
    if (!isEditable) {
      if (isControlled) {
        onEditEnd?.()
      } else {
        setInternalIsEditing(false)
      }
      return
    }

    if (isControlled) {
      onEditEnd?.()
    } else {
      setInternalIsEditing(false)
    }

    // Only process changes if the user actually typed something
    if (!hasUserInput) {
      setTempValue(value?.toString() || ``)
      return
    }

    // If tempValue is empty or unchanged, preserve the original value
    if (tempValue.trim() === `` || tempValue === (value?.toString() || ``)) {
      setTempValue(value?.toString() || ``)
      return
    }

    // Only update if there's a valid change
    const numValue = tempValue ? parseFloat(tempValue) : null
    if (numValue !== null && !isNaN(numValue) && numValue !== value) {
      // Round to 2 decimal places
      const roundedValue = Math.round(numValue * 100) / 100
      onValueChange(roundedValue)
    } else {
      // Reset to original value if parsing failed
      setTempValue(value?.toString() || ``)
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === `Enter`) {
      handleSave()
    } else if (e.key === `Escape`) {
      setTempValue(value?.toString() || ``)
      if (isControlled) {
        onEditEnd?.()
      } else {
        setInternalIsEditing(false)
      }
    } else if (e.key === `Tab`) {
      e.preventDefault()
      handleSave()
      if (e.shiftKey) {
        onTabPrev?.()
      } else {
        onTabNext?.()
      }
    }
  }

  const getBgColor = () => {
    switch (source) {
      case `user`:
        return `bg-green-100 border-green-200`
      case `estimated`:
        return `bg-gray-100 border-gray-200`
      default:
        return `bg-white border-gray-200`
    }
  }

  const getTextColor = () => {
    switch (source) {
      case `user`:
        return `text-green-800`
      case `estimated`:
        return `text-gray-700`
      default:
        return `text-gray-400`
    }
  }

  const displayValue = value !== null ? value.toFixed(0) : `-`

  return (
    <div
      className={`group relative flex min-h-[80px] ${
        isEditable ? `cursor-pointer` : `cursor-not-allowed`
      } flex-col justify-between rounded-lg border-2 p-4 transition-all ${getBgColor()} ${
        isEditable ? `hover:border-green-500 hover:bg-white hover:shadow-md` : ``
      }`}
      onClick={!isEditing && isEditable ? handleClick : undefined}
      onMouseEnter={isEditable ? () => setIsHovered(true) : undefined}
      onMouseLeave={isEditable ? () => setIsHovered(false) : undefined}
    >
      <div>
        <div
          className={`text-xs font-semibold tracking-wide uppercase ${getTextColor()}`}
        >
          {month} ({displayUnit})
        </div>
        <div
          className={`flex min-h-[24px] items-center justify-center text-lg font-medium ${getTextColor()}`}
        >
          {isEditing && isEditable ? (
            <input
              ref={inputRef}
              type="number"
              value={getInputDisplayValue()}
              onChange={(e) => {
                setTempValue(e.target.value)
                setHasUserInput(true)
              }}
              onBlur={handleSave}
              onKeyDown={handleKeyDown}
              className={`w-full border-none bg-transparent text-center text-lg font-medium outline-none ${getTextColor()}`}
              style={{ padding: 0, margin: 0, lineHeight: `inherit` }}
              placeholder={displayUnit === `$` ? `500` : `1200`}
            />
          ) : (
            <span>
              {displayUnit === `$` && displayValue !== `-` ? `$` : ``}
              {displayValue}
              {displayUnit === `kWh` && displayValue !== `-` ? `` : ``}
            </span>
          )}
        </div>
      </div>
      {/* Edit icon at bottom - minimal reserved space, visible on hover */}
      <div className="flex h-3.5 items-center justify-center gap-1 text-green-600">
        {isHovered && !isEditing && isEditable && (
          <>
            <PencilIcon className="h-3.5 w-3.5" />
            <span className="text-xs font-medium">Edit</span>
          </>
        )}
      </div>
    </div>
  )
}
